import { forwardRef, useMemo } from 'react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess';
import { filterLocationIdsByACLs } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { useAppScopeStore } from '@frontend/scope';
import { useAppFlagStore } from '@frontend/shared';
import type { LocationPhoneSelection, PersonPhoneSelection } from '@frontend/thread-header-context';
import { InboxPrefixes } from '@frontend/tracking-prefixes';
import { CommonHTMLAttributes } from '@frontend/types';
import { theme } from '@frontend/theme';
import { Button, PopoverMenu, Text, Truncated, usePopoverMenu } from '@frontend/design-system';
import { LocationPhoneSelectionPopoverMenuContents } from './location-phone-selection-popover-menu-contents';
import { useBatchGetThreadStatusQuery } from './use-batch-get-thread-status-query';

export type LocationPhoneSelectorProps = {
  selectedPersonPhone: PersonPhoneSelection;
  selectedLocationPhone: LocationPhoneSelection;
  onSelectLocationPhone: (selection: LocationPhoneSelection, threadId?: string) => void;
  variant?: 'normal' | 'small';
  color?: 'white' | 'default';
  hideCurrentLocationPhone?: boolean;
  popoverState?: ReturnType<typeof usePopoverMenu>;
} & CommonHTMLAttributes;

export const LocationPhoneSelector = forwardRef<HTMLSpanElement, LocationPhoneSelectorProps>(
  (
    {
      selectedPersonPhone,
      selectedLocationPhone,
      onSelectLocationPhone,
      variant = 'normal',
      color = 'default',
      hideCurrentLocationPhone = false,
      popoverState: providedPopoverState,
      ...rest
    },
    ref
  ) => {
    const { t } = useTranslation('thread-header');
    const { selectedLocationIds } = useAppScopeStore();
    const locationIdsWithACL = filterLocationIdsByACLs({
      locationIds: selectedLocationIds,
      acls: Permission.SMS_INBOX_MANAGE,
    });
    const isMultiLocation = locationIdsWithACL.length > 1;
    const { availableConversationsNumbersByLocation, locationPhonesQuery } = useBatchGetThreadStatusQuery(
      selectedPersonPhone?.personPhone
    );
    const isLoading = locationPhonesQuery.isLoading;
    const selectedLocationIdAvailableNumbers = selectedLocationPhone.locationId
      ? (availableConversationsNumbersByLocation[selectedLocationPhone.locationId] || []).map(
          (number) => number.locationPhone
        )
      : [];
    const hasMultipleAvailableNumbersForSelectedLocation = selectedLocationIdAvailableNumbers.length > 1;
    const allLocationPhones = useMemo(() => locationPhonesQuery.data.allLocationPhones || [], [locationPhonesQuery]);
    const buttonLabel = useMemo(() => {
      const matchingPhone = allLocationPhones.find(
        (phone) => phone.locationPhone === selectedLocationPhone.locationPhone
      );
      if (!matchingPhone) return formatPhoneNumber(selectedLocationPhone.locationPhone, true);

      return `${matchingPhone.displayName ?? ''} ${formatPhoneNumber(matchingPhone.locationPhone, true)}`.trim();
    }, [allLocationPhones, selectedLocationPhone.locationPhone]);

    const internalPopoverState = usePopoverMenu({ placement: 'bottom-start' });
    const popoverState = providedPopoverState || internalPopoverState;
    const hasLocationPhoneOptions = allLocationPhones.length > 1;

    // TODO: remove this logic when we deprecate the `phone-numbers-name-id` FF
    const { selectedLocationIds: locationIds } = useAppScopeStore();
    const { getFeatureFlagValue } = useAppFlagStore();
    const shouldUseV4 = getFeatureFlagValue({
      locationIds,
      flagName: 'phone-numbers-name-id',
      strategy: 'ALL',
    });
    // END OF TODO

    if (!hasLocationPhoneOptions || (shouldUseV4 && isLoading)) {
      return null;
    }

    // show popover if there are multiple available numbers for the selected location
    // or if we're using the v3 experience (which always shows the popover)
    const showPopover = hasMultipleAvailableNumbersForSelectedLocation || !shouldUseV4 || isMultiLocation;

    return (
      <>
        <span
          css={{
            display: 'flex',
            alignItems: 'center',
            gap: 0,
            justifyContent: 'flex-start',
            overflowX: 'hidden',
            textOverflow: 'ellipsis',
          }}
          {...rest}
          data-isopen={popoverState.isOpen}
          data-testid='inbox-thread-header-location-phone-selector'
          ref={ref}
        >
          {variant === 'normal' && (
            <Text color={color === 'white' ? 'white' : 'light'} size='medium'>
              {t('From:')}
            </Text>
          )}
          <Button
            variant={variant === 'small' ? 'secondary' : 'tertiary'}
            iconName={variant === 'small' ? 'arrow-right-small' : undefined}
            css={[
              variant === 'small' && {
                border: 'none',
                svg: {
                  rotate: '135deg',
                },
              },
              { padding: theme.spacing(1, 0.75) },
              !showPopover && { cursor: 'default', pointerEvents: 'none' },
            ]}
            {...(showPopover && popoverState.getTriggerProps())}
            trackingId={`${InboxPrefixes.Thread}-from-location-phone-selector-button`}
          >
            {variant === 'normal' && (
              <Truncated
                as={Text}
                size='medium'
                color={showPopover ? 'primary' : 'default'}
                css={{ maxWidth: '200px', fontWeight: 'normal' }}
              >
                {buttonLabel}
              </Truncated>
            )}
            {showPopover && (
              <Icon
                name='caret-down-tiny'
                color={color === 'white' ? 'white' : 'light'}
                style={{
                  rotate: popoverState.isOpen ? '-180deg' : '0deg',
                  transition: 'rotate 200ms ease-in-out',
                  minWidth: 'min-content',
                }}
              />
            )}
          </Button>
          <PopoverMenu
            {...popoverState.getMenuProps()}
            css={[{ maxWidth: 300 }, shouldUseV4 && { maxHeight: '400px !important', padding: 0 }]}
            alwaysRender
          >
            <LocationPhoneSelectionPopoverMenuContents
              activeIndex={popoverState.activeIndex ?? -1}
              getItemProps={popoverState.getItemProps}
              hideCurrentLocationPhone={hideCurrentLocationPhone}
              isOpen={popoverState.isOpen}
              onSelectLocationPhone={(locationPhone, locationId, threadId) => {
                onSelectLocationPhone({ locationPhone, locationId }, threadId);
                popoverState.close();
              }}
              selectedLocationPhone={selectedLocationPhone}
              selectedPersonPhone={selectedPersonPhone}
            />
          </PopoverMenu>
        </span>
      </>
    );
  }
);
LocationPhoneSelector.displayName = 'LocationPhoneSelector';
